import type { ICampaignGroup, ICampaignGroupPrice } from '~/modules/campaign-group';
import type { ICampaignProduct } from '~/modules/campaign-product';
import type { GCGActionButtonProps } from './types';

import {
  campaignAllowedSyncWithLivePages,
  useCampaignStore,
  useModalPublishCampaignStore,
  usePublishTemplates,
} from '~/modules/campaign';
import { GModalConfirmType, useGModalConfirm } from '~/presentation/common';
import { useCGTemplateContext } from '~/modules/campaign-group-template';
import { useMutationCampaignGroupState } from '~/modules/campaign-group';
import { useCampaignActionTooltip } from '~/modules/campaign-template';
import { CAMPAIGN_STATUS, CAMPAIGN_TYPE } from '~/constants/campaign';
import { PauseCircleIcon, PlayIcon } from '@shopify/polaris-icons';
import { GCampaignActionLocked } from './GCampaignActionLocked';
import { useCGCampaignLimitByStatus } from './hooks';
import { useAppConfigStore } from '~/modules/apps';
import { useSaveBar } from '~/hooks/useSaveBar';
import { useTranslation } from 'react-i18next';
import { GButton } from '~/presentation/ui';
import { useUnsavedGuard } from '~/hooks';
import { useMemo, useState } from 'react';

export interface GCGTemplateActionByStateProps {
  campaignGroup: ICampaignGroup;
  isChanged?: boolean;
  buttonProps?: GCGActionButtonProps;
  onSaveCampaign?: () => Promise<void>;
  onAction?: (campaignGroup: ICampaignGroup) => void;
}

export const GCGTemplateActionByState = ({
  campaignGroup,
  buttonProps,
  isChanged,
  onSaveCampaign,
  onAction,
}: GCGTemplateActionByStateProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const isShowBanner = useAppConfigStore((state) => state.isShowBanner);
  const isLoadingUpdateState = useCampaignStore((state) => state.isLoadingUpdateState);
  const setIsLoadingUpdateState = useCampaignStore((state) => state.setIsLoadingUpdateState);

  const { t } = useTranslation();
  const { handleOpenModalConfirm } = useGModalConfirm();
  const { handleUpdateCampaignGroupState } = useMutationCampaignGroupState();
  const { handleSetCampaignGroup, campaignGroupCampaigns } = useCGTemplateContext();
  const { lockedStatus } = useCGCampaignLimitByStatus();
  const { shopifySaveBar } = useSaveBar();
  const { withUnsavedGuard } = useUnsavedGuard(!!isChanged);

  const isExistsVariantEmpty = useMemo(() => {
    const isExistsVariantEmpty = !!campaignGroupCampaigns?.find((item) =>
      item?.variants?.find((variant) => !variant?.templateSuffix),
    );

    return !campaignGroupCampaigns?.length || isExistsVariantEmpty;
  }, [campaignGroupCampaigns]);

  const isDisabledActive = useMemo(() => {
    if (isLoadingUpdateState) return true;
    if (campaignAllowedSyncWithLivePages(campaignGroup)) return true;
    return isExistsVariantEmpty;
  }, [campaignGroup, isLoadingUpdateState, isExistsVariantEmpty]);

  const openModalPublishAndStartCampaign = useModalPublishCampaignStore(
    (state) => state.onOpenModal,
  );

  const { publishTemplates, unPublishedTemplateIdsB, isPublishing, isPublishingByCampaign } =
    usePublishTemplates({
      callBack: () => onChangeStateCampaign(CAMPAIGN_STATUS.RUNNING),
      campaigns: campaignGroupCampaigns,
    });
  const { startButtonTooltip, hasUnpublishedOrDeleted } = useCampaignActionTooltip({
    campaign: campaignGroup,
    isExistsVariantEmpty,
  });

  async function onChangeStateCampaign(status: ICampaignGroup['status']) {
    try {
      onHandleLoading(true);

      if (isChanged && onSaveCampaign) await onSaveCampaign();
      const updateCampaign: ICampaignGroupPrice = {
        ...campaignGroup,
        status,
        type: CAMPAIGN_TYPE.TEMPLATE_BUNDLE as ICampaignGroupPrice['type'],
        campaigns: campaignGroup.campaigns?.map((campaign) => ({
          ...campaign,
          status,
        })) as ICampaignProduct[],
      };
      await handleUpdateCampaignGroupState(updateCampaign);

      handleSetCampaignGroup(updateCampaign);
      onAction?.(updateCampaign);
    } catch (error) {
      throw new Error((error as Error).message);
    } finally {
      onHandleLoading(false);
    }
  }

  const onRunningCampaign = () => {
    if (isDisabledActive || isLoadingUpdateState) return;

    if (isShowBanner) {
      handleOpenModalConfirm({
        type: GModalConfirmType.THEME_EXTENSION,
      });
      return;
    }

    if (unPublishedTemplateIdsB.length > 0) {
      openModalPublishAndStartCampaign(unPublishedTemplateIdsB, () => {
        publishTemplates();
      });
      return;
    }

    const isRestartCampaign = campaignGroup.status === CAMPAIGN_STATUS.PAUSED;
    if (!isRestartCampaign) {
      onChangeStateCampaign(CAMPAIGN_STATUS.RUNNING);
      return;
    }

    handleOpenModalConfirm({
      id: campaignGroup.id,
      title: t('Continue test experiment'),
      message: t(
        'Resume the test will reset all the previous data. Are you sure you want to proceed?',
      ),
      primaryAction: {
        content: t('Continue test experiment'),
        onAction: () => onChangeStateCampaign(CAMPAIGN_STATUS.RUNNING),
      },
    });
  };

  const onPauseCampaign = () => {
    if (isChanged) {
      shopifySaveBar?.leaveConfirmation();
      return;
    }

    handleOpenModalConfirm({
      id: campaignGroup.id,
      title: t('Pause experiment'),
      message: t('Once the test is paused, the live page displays the original template.'),
      primaryAction: {
        content: t('Pause experiment'),
        onAction: () => onChangeStateCampaign(CAMPAIGN_STATUS.PAUSED),
      },
    });
  };
  const onHandleLoading = (value: boolean) => {
    setIsLoading(value);
    setIsLoadingUpdateState(value);
  };

  switch (campaignGroup.status) {
    case CAMPAIGN_STATUS.PAUSED:
    case CAMPAIGN_STATUS.DRAFT:
      if (lockedStatus && !isLoading) {
        return <GCampaignActionLocked lockedStatus={lockedStatus} buttonProps={buttonProps} />;
      }

      return (
        <GButton
          {...buttonProps}
          disabled={
            isDisabledActive || isLoadingUpdateState || isPublishing || hasUnpublishedOrDeleted
          }
          loading={isLoading || isPublishingByCampaign}
          onClick={withUnsavedGuard(onRunningCampaign)}
          icon={PlayIcon}
          {...(buttonProps?.isShowText
            ? { children: t('Start experiment'), tooltip: { content: startButtonTooltip } }
            : { tooltip: { content: startButtonTooltip || t('Start experiment') } })}
        ></GButton>
      );

    case CAMPAIGN_STATUS.RUNNING:
      return (
        <GButton
          {...buttonProps}
          loading={isLoading}
          disabled={isChanged}
          onClick={onPauseCampaign}
          icon={PauseCircleIcon}
          {...(buttonProps?.isShowText
            ? { children: t('Pause experiment') }
            : { tooltip: { content: t('Pause experiment') } })}
        ></GButton>
      );

    case CAMPAIGN_STATUS.COMPLETED:
    case CAMPAIGN_STATUS.STOPPED:
      return (
        <GButton
          {...buttonProps}
          loading={isLoading}
          disabled
          icon={PlayIcon}
          {...(buttonProps?.isShowText ? { children: t('Start experiment') } : {})}
        ></GButton>
      );
  }

  return '';
};
